import "./index.scss";
import { Button, LinkAsButton } from "@wise-emotions/react-button";
import { FieldValues, useForm } from "react-hook-form";
import {
  PageExtraProps,
  RangeVal,
  ResponseCode,
  SurveyNPSRespose,
  SurveyResponseProps,
} from "./types";
import { Redirect } from "@reach/router";
import { TextArea } from "@wise-emotions/react-text-area";
import { parse } from "qs";
import Loader from "components/Loader";
import React, { useState } from "react";
import axios, { AxiosResponse } from "axios";
import cn from "classnames";

const API_SURVEY = `${process.env.GATSBY_SURVEY_PATH_API}/kxopega/SurveyNotify?apikey=${process.env.GATSBY_SURVEY_APIKEY}`;

const MAX_LENGTH_NOTE = 300;
const RANGE_VOTES: RangeVal[] = [
  {
    value: "1",
    emoji: "\u{1F622}",
  },
  {
    value: "2",
    emoji: "\u{1F615}",
  },
  {
    value: "3",
    emoji: "\u{1F642}",
  },
  {
    value: "4",
    emoji: "\u{1F604}",
  },
  {
    value: "5",
    emoji: "\u{1F60D}",
  },
];

const SurveyResponse = ({
  text,
  title,
  status,
  labelCtaResponse,
}: SurveyResponseProps) => {
  return (
    <div className="survey-nps-response">
      <div className="survey-nps-response__image">
        {status === "00" ? (
          <img src="/animation_success.gif" alt="succes" />
        ) : (
          <img src="/animation_error.gif" alt="error" />
        )}
      </div>
      <div className="survey-nps-response__title">{title}</div>
      <div className="survey-nps-response__text">{text}</div>
      <LinkAsButton href="/">{labelCtaResponse}</LinkAsButton>
    </div>
  );
};

const SurveyNPS = ({ page }: PageExtraProps) => {
  let token: string | undefined = "";
  if (typeof window !== "undefined" && window.location.search) {
    const obj = parse(window.location.search, { ignoreQueryPrefix: true });
    const { t: inputFromQs } = obj;
    token = inputFromQs as string | undefined;
  }

  const {
    title,
    headingTitle,
    text,
    textAreaLabel,
    textAreaLabelError,
    labelBtnVote,
    successTitle,
    successText,
    errorTitle,
    errorText,
    errorAlreadyExist,
    errorMissingData,
    dataTracking,
    labelCtaResponse,
  } = page?.content[0] || {};

  const getResponseMessage = (code: ResponseCode) => {
    // 00 success, 01 aleadyexist, 02 missing data, 99 generic error
    const messages = {
      "00": {
        title: successTitle,
        text: successText,
      },
      "01": {
        title: errorTitle,
        text: errorAlreadyExist,
      },
      "02": {
        title: errorTitle,
        text: errorMissingData,
      },
      "99": {
        title: errorTitle,
        text: errorText,
      },
    };
    return messages[code];
  };

  const [loader, setLoader] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();
  const [statusResponse, setStatusResponse] = useState<ResponseCode>();

  const currentNote = watch("note") || 0;
  const currentScore = watch("score");

  const onSubmit = (data: FieldValues) => {
    if (token) {
      const dataSent: FieldValues = { ...data, ...{ token } };
      setLoader(true);
      setTimeout(() => {
        axios(API_SURVEY, {
          method: "POST",
          data: dataSent,
        })
          .then((res: AxiosResponse<SurveyNPSRespose>) => {
            setLoader(false);
            setStatusResponse(res.data.esito);
          })
          .catch(() => {
            setLoader(false);
            setStatusResponse("99");
          });
      }, 4000);
    }
  };

  const { node_locale } = page;
  if (!token) {
    return <Redirect noThrow to={`/${node_locale}/404`} />;
  }

  return (
    <div className="survey-nps">
      <div className="survey-nps__box">
        <div className="survey-nps__box__wrap">
          {loader && (
            <div className="survey-nps__loader">
              <Loader loading={loader} />
            </div>
          )}
          {!statusResponse ? (
            <>
              <h3 className="survey-nps__top-title">{headingTitle}</h3>
              <h1 className="survey-nps__title">{title}</h1>
              <div className="survey-nps__text">
                <p>{text?.text}</p>
              </div>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="survey-nps__form"
              >
                <div className="survey-nps__range">
                  {RANGE_VOTES.map((item) => {
                    return (
                      <div
                        className={cn("survey-nps__range-value", {
                          "survey-nps__range-value--active":
                            item.value === currentScore,
                        })}
                        key={item.value}
                      >
                        <label
                          htmlFor={`score-${item.value}`}
                          className="survey-nps__range-value__label"
                        >
                          <input
                            className="survey-nps__range-value__radio"
                            id={`score-${item.value}`}
                            type="radio"
                            value={item.value}
                            {...register("score")}
                          />
                          <span
                            role="img"
                            className="survey-nps__range-value__pointer"
                            aria-label={item.value}
                          >
                            {item.emoji}
                          </span>
                        </label>
                      </div>
                    );
                  })}
                </div>
                <div className="survey-nps__textarea">
                  <TextArea
                    id="note"
                    label={textAreaLabel}
                    hideLabel
                    placeholder={textAreaLabel}
                    maxLength={MAX_LENGTH_NOTE}
                    removeHelperText
                    {...register("note")}
                  />
                  {currentNote.length >= MAX_LENGTH_NOTE && (
                    <div className="survey-nps__textarea__error">
                      {textAreaLabelError}
                    </div>
                  )}
                </div>
                <div data-tracking={dataTracking}>
                  <Button
                    variant="default"
                    type="submit"
                    disabled={!currentScore}
                  >
                    {labelBtnVote}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <SurveyResponse
              text={getResponseMessage(statusResponse).text}
              title={getResponseMessage(statusResponse).title}
              status={statusResponse}
              labelCtaResponse={labelCtaResponse}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyNPS;
